// =============================================================================
// charConfidences → suspect-character spans (OCR ADR-013 consumer side).
//
// Converts the wire contract — UTF-16 {start,length,confidence} spans over a
// region's final text — into the CODE-POINT CharSpans the VS HighlightedText
// renderer consumes (Array.from segmentation, same as alignCandidates).
//
// SECONDARY signal by contract: the output only ever tints characters to draw
// the keyer's eye. It must never gate acceptance, block keying, or auto-reject
// a field — engines self-report confidence poorly when hallucinating, so a
// fully-confident wrong read exists.
// =============================================================================
/**
 * Characters whose span confidence is STRICTLY below this are tinted suspect.
 * Eval evidence (48-scan set): erroneous chars mean 0.480, correct 0.863 —
 * the 0.60–0.70 band splits them with margin on both sides.
 */
export const SUSPECT_CONFIDENCE_THRESHOLD = 0.65;
/**
 * Filter charConfidences to sub-threshold spans and convert UTF-16 offsets to
 * code-point CharSpans, merging contiguous runs. Null/absent input → [] (the
 * field renders exactly like today). Out-of-range spans are clamped, never
 * thrown — the renderer must survive server drift.
 */
/** True when any span's confidence is strictly below the threshold. */
export function hasSuspectSpan(spans, threshold = SUSPECT_CONFIDENCE_THRESHOLD) {
    return !!spans && spans.some((s) => s.confidence < threshold);
}
export function suspectSpansFromCharConfidences(text, spans, threshold = SUSPECT_CONFIDENCE_THRESHOLD) {
    // Early out before building the code-point index — most fields that carry
    // spans are fully confident. (Explicit null check so TS narrows `spans`.)
    if (!spans || !hasSuspectSpan(spans, threshold))
        return [];
    // Map every UTF-16 offset to its code-point index. Length utf16.length + 1
    // so an exclusive end offset at the string end resolves.
    const cpIndexAtUtf16 = new Array(text.length + 1);
    let cp = 0;
    let u = 0;
    for (const ch of text) {
        for (let k = 0; k < ch.length; k++)
            cpIndexAtUtf16[u + k] = cp;
        u += ch.length;
        cp++;
    }
    cpIndexAtUtf16[text.length] = cp;
    const totalCp = cp;
    const out = [];
    for (const span of spans) {
        if (span.confidence >= threshold)
            continue;
        const startU = Math.max(0, Math.min(span.start, text.length));
        const endU = Math.max(startU, Math.min(span.start + span.length, text.length));
        if (endU === startU)
            continue;
        const start = cpIndexAtUtf16[startU];
        const end = Math.min(cpIndexAtUtf16[endU], totalCp);
        if (end <= start)
            continue;
        const last = out[out.length - 1];
        if (last && last.end >= start) {
            last.end = Math.max(last.end, end);
        }
        else {
            out.push({ start, end });
        }
    }
    return out;
}
//# sourceMappingURL=suspect-spans.js.map